import moment from 'moment'
import { type IntlShape } from 'react-intl'

import {
  OTHER_PAYMENT_OPTION_ID,
  type RequestableCreditCardOption,
} from '@webapp/components/RequestableCreditCardSelect'
import { type Currency } from '@webapp/core/entities/external/Currency'
import {
  computeExpenseLineItemTotalsUseCase,
  parsePercentageOrZero,
  round2,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import {
  ExpenseFormReimbursementCustomizations,
  ExpenseMileageConfigurationTaxTypes,
  MileageRatePresetUnits,
  UnitsOfDistance,
  type ExpenseFormReimbursementCustomization,
} from '@webapp/modules/expense/entities/enums'
import { ExpensePaymentTypes } from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { ExpenseTypes } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { type RequestMileageConfiguration } from '@webapp/modules/expense/entities/RequestMileageConfiguration'
import { type Tax } from '@webapp/platform/finance'
import { type ExpenseLineItemFormSchema } from './ExpenseLineItemForm'
import messages from './messages'
import {
  type ExpenseMileageConfiguration,
  type MileageRatePreset,
} from '../../entities'
import { buildMileageRatePreset } from '../../utils'
import { type ExpenseTaxSelectValue, toTaxMode } from '../ExpenseTaxSelect'

interface MapExpenseItemToFormSchemaParams {
  item: ExpenseItem | undefined
  baseCurrency: Currency | null | undefined
  intl: IntlShape
  reimbursementSetting: ExpenseFormReimbursementCustomization
  attachment: string
  mileageConfiguration?: Nullable<ExpenseMileageConfiguration>
  mileageLockSetting?: RequestMileageConfiguration | null
  PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS?: boolean
}

const resolveInitialReimbursable = (
  item: ExpenseItem | undefined,
  reimbursementSetting: ExpenseFormReimbursementCustomization
): boolean => {
  if (
    reimbursementSetting === ExpenseFormReimbursementCustomizations.ALWAYS_YES
  )
    return true
  if (reimbursementSetting === ExpenseFormReimbursementCustomizations.ALWAYS_NO)
    return false
  if (item != null) return item.reimbursable
  return (
    reimbursementSetting === ExpenseFormReimbursementCustomizations.DEFAULT_YES
  )
}

const resolveMealsData = (item: ExpenseItem | undefined) => {
  if (item != null && item.expenseTypeData.type === 'MEALS') {
    return item.expenseTypeData.data
  }
  return { tips: 0 }
}

const resolveMileagePreset = (
  item: ExpenseItem | undefined,
  mileageConfiguration: Nullable<ExpenseMileageConfiguration>
): Nullable<MileageRatePreset> => {
  if (item?.expenseTypeData?.type === 'MILEAGE') {
    const reconstructed = buildMileageRatePreset({
      id: item.mileageRatePresetId,
      name: item.mileageRatePresetName,
      rate: item.expenseTypeData.data.mileageRate,
      unitOfDistance: item.expenseTypeData.data.unitOfDistance,
      advisoryFuelRate: item.expenseTypeData.data.advisoryFuelRate,
      currency: item.currency,
    })

    if (
      reconstructed ||
      (item.expenseTypeData.data.mileageRate &&
        !mileageConfiguration?.forcePresetRates)
    ) {
      return reconstructed
    }
  }
  return mileageConfiguration?.defaultMileagePreset ?? null
}

const resolveMileageData = (
  item: ExpenseItem | undefined,
  mileageConfiguration: Nullable<ExpenseMileageConfiguration>
) => {
  if (item != null && item.expenseTypeData.type === 'MILEAGE') {
    const isCustomPresetRateSet =
      !item.mileageRatePresetId && item.expenseTypeData.data.mileageRate

    if (mileageConfiguration?.forcePresetRates && isCustomPresetRateSet) {
      return {
        ...item.expenseTypeData.data,
        mileageRate: mileageConfiguration?.defaultMileagePreset?.rate ?? 0,
        unitOfDistance:
          mileageConfiguration?.defaultMileagePreset?.unit ===
          MileageRatePresetUnits.MILES
            ? UnitsOfDistance.MILES
            : UnitsOfDistance.KILOMETERS,
      }
    }

    return item.expenseTypeData.data
  }

  return {
    mileageRate: mileageConfiguration?.defaultMileagePreset?.rate ?? 0,
    distance: 0,
    unitOfDistance:
      mileageConfiguration?.defaultMileagePreset?.unit ===
      MileageRatePresetUnits.MILES
        ? UnitsOfDistance.MILES
        : UnitsOfDistance.KILOMETERS,
  }
}

/**
 * to be removed with PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
 */
const resolveMileageDataDeprecated = (
  item: ExpenseItem | undefined,
  mileageLockSetting: RequestMileageConfiguration | null
) => {
  if (item != null && item.expenseTypeData.type === 'MILEAGE') {
    return item.expenseTypeData.data
  }
  // For new items, seed rate from the system mileage lock when present
  return {
    mileageRate: mileageLockSetting?.rate ?? 0,
    distance: 0,
    unitOfDistance: UnitsOfDistance.KILOMETERS,
  }
}

const resolveLabourData = (item: ExpenseItem | undefined) => {
  if (item != null && item.expenseTypeData.type === 'LABOUR') {
    return item.expenseTypeData.data
  }
  return { hourlyRate: 0, hoursOfWork: 0 }
}

const resolveAirfareData = (item: ExpenseItem | undefined) => {
  if (item != null && item.expenseTypeData.type === 'AIRFARE') {
    return item.expenseTypeData.data
  }
  return {
    ticketNumber: '',
    arrivalDate: null,
    departureDate: null,
    airline: '',
  }
}

const resolveInitialCurrency = (
  item: ExpenseItem | undefined,
  mileageConfiguration: Nullable<ExpenseMileageConfiguration>
): Currency | null => {
  if (item?.currency) return item.currency

  if (
    item?.expenseType === ExpenseTypes.MILEAGE &&
    mileageConfiguration?.defaultMileagePreset
  ) {
    return mileageConfiguration?.defaultMileagePreset.currency
  }

  return null
}

const resolveInitialCurrencyDeprecated = (
  item: ExpenseItem | undefined,
  mileageLockSetting: RequestMileageConfiguration | null
): Currency | null => {
  if (item?.currency) return item.currency

  if (
    item?.expenseType === ExpenseTypes.MILEAGE &&
    mileageLockSetting?.currency
  ) {
    return mileageLockSetting?.currency
  }

  return null
}

const resolveInitialTaxType = (
  item: ExpenseItem | undefined,
  intl: IntlShape
): ExpenseTaxSelectValue => {
  if (item?.taxId != null) {
    // custom tax selected on the item — reconstruct a Tax from the item
    // fields so the Select hydrates with the same option on remount.
    const reconstructed: Tax = {
      id: item.taxId,
      name: item.taxName,
      percentage: item.taxPercentage,
      description: '',
      isActive: true,
      isDefault: false,
      isInclusive: false,
    }
    return reconstructed
  }
  if (item?.taxName === '$') {
    return { id: '$', name: intl.formatMessage(messages.taxTypeDollar) }
  }
  return { id: '%', name: intl.formatMessage(messages.taxTypePercent) }
}

const resolveInitialTaxAmount = (
  item: ExpenseItem | undefined,
  mileageConfiguration: Nullable<ExpenseMileageConfiguration>
): number => {
  if (item == null) return 0

  if (
    item.expenseType === ExpenseTypes.MILEAGE &&
    mileageConfiguration?.taxType === ExpenseMileageConfigurationTaxTypes.NO_TAX
  )
    return 0

  if (item.taxId != null) {
    // custom tax: the displayed taxAmount is the computed dollar tax
    // (subtotal × percentage), matching what TaxFields renders.
    return round2(item.subtotal * parsePercentageOrZero(item.taxPercentage))
  }
  if (item.taxName === '$') return item.taxAmount
  const pct = parsePercentageOrZero(item.taxPercentage)
  // Snap to cents: IEEE 754 would otherwise produce values like
  // 13.000000000000002 for a stored '0.13000000', which leaks into the
  // displayed taxAmount field.
  return Math.round(pct * 10000) / 100
}

const resolveInitialPaymentMethod = (
  item: ExpenseItem | undefined,
  intl: IntlShape
): RequestableCreditCardOption | null => {
  if (item?.paymentCard != null) {
    // Map SpendingCard to a display-only option for RequestableCreditCardSelect.
    // The Select only uses id/name/number for display; type/currency/vendor are
    // null because the expense detail API does not provide full credit card details.
    return {
      id: item.paymentCard.id,
      name: item.paymentCard.name,
      number: item.paymentCard.last4Digits,
      type: null,
      currency: null,
      vendor: null,
    } as RequestableCreditCardOption
  }
  if (item?.paymentType === ExpensePaymentTypes.OTHER) {
    return {
      id: OTHER_PAYMENT_OPTION_ID,
      name: intl.formatMessage(messages.paymentMethodOther),
      number: intl.formatMessage(messages.paymentMethodOtherDescription),
      type: null,
      currency: null,
      vendor: null,
    } as RequestableCreditCardOption
  }
  return null
}

export const mapExpenseItemToFormSchema = ({
  item,
  baseCurrency,
  intl,
  reimbursementSetting,
  attachment,
  mileageConfiguration = null,
  mileageLockSetting = null,
  PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = false,
}: MapExpenseItemToFormSchemaParams): ExpenseLineItemFormSchema => {
  const mealsData = resolveMealsData(item)
  const mileagePreset = resolveMileagePreset(item, mileageConfiguration)
  const mileageData = PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
    ? resolveMileageData(item, mileageConfiguration)
    : resolveMileageDataDeprecated(item, mileageLockSetting)
  const labourData = resolveLabourData(item)
  const airfareData = resolveAirfareData(item)

  const initialExpenseType = item?.expenseType ?? ExpenseTypes.OTHER
  const initialSubtotal = item?.subtotal ?? 0
  const initialTaxAmount = resolveInitialTaxAmount(item, mileageConfiguration)
  const initialTaxType = resolveInitialTaxType(item, intl)

  const initialCurrency = PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
    ? resolveInitialCurrency(item, mileageConfiguration)
    : resolveInitialCurrencyDeprecated(item, mileageLockSetting)

  // Seed totalCost from the forward compute so the mount state is already
  // consistent. Otherwise the forward sync effect would flash a 0 before
  // recomputing, and the reverse effect would momentarily back-solve subtotal
  // from that 0.
  const { computedTotal: initialTotalCost } =
    computeExpenseLineItemTotalsUseCase.execute({
      expenseType: initialExpenseType,
      subtotal: initialSubtotal,
      tax: toTaxMode(initialTaxType, initialTaxAmount),
      tips: mealsData.tips,
      mileageRate: mileageData.mileageRate,
      distance: mileageData.distance,
      hourlyRate: labourData.hourlyRate,
      hoursOfWork: labourData.hoursOfWork,
    })

  return {
    reimbursable: resolveInitialReimbursable(item, reimbursementSetting),
    merchant: item?.merchant ?? '',
    transDate: item != null ? moment(item.transactionDate) : moment(),
    subtotal: initialSubtotal,
    taxAmount: initialTaxAmount,
    taxType: initialTaxType,
    notes: item?.notes ?? '',
    expenseType: initialExpenseType,
    tips: mealsData.tips,
    mileageRate: mileageData.mileageRate,
    mileageRatePreset: mileagePreset,
    distance: mileageData.distance,
    unitOfDistanceStr: {
      value: mileageData.unitOfDistance,
      label:
        mileageData.unitOfDistance === UnitsOfDistance.KILOMETERS
          ? intl.formatMessage(messages.unitKm)
          : intl.formatMessage(messages.unitMi),
    },
    hourlyRate: labourData.hourlyRate,
    hoursOfWork: labourData.hoursOfWork,
    airline: airfareData.airline,
    ticketNumber: airfareData.ticketNumber,
    departureDate: airfareData.departureDate
      ? moment(airfareData.departureDate)
      : null,
    arrivalDate: airfareData.arrivalDate
      ? moment(airfareData.arrivalDate)
      : null,
    account: item?.account ?? null,
    attachment,
    paymentMethod: resolveInitialPaymentMethod(item, intl),
    currency: initialCurrency ?? baseCurrency ?? null,
    totalCost: initialTotalCost,
  }
}
